export function electronicFormat(iban: string) {
  return iban.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
}

export function amountToUPNString(inputAmount: number) {
  // Convert "4,1" to "4.1" if needed
  const normalized = inputAmount.toString().replace(",", ".");

  // Parse as float
  const amount = parseFloat(normalized);

  // Convert to cents and round
  const amountCents = Math.round(amount * 100);

  // Convert to an 11-digit zero-padded string
  return amountCents.toString().padStart(11, "0");
}

// The UPN QR standard (ZBS "Tehnični standard UPN QR") caps the payload at
// 411 characters including all LF separators; padding the trailing reserve
// field with spaces up to that cap forces auto-versioning QR encoders
// (including ESC/POS printer firmware) to emit the mandated version 15
// (77x77) symbol.
export const UPN_QR_MAX_PAYLOAD_LENGTH = 411;

// Characters at ISO 8859-2 positions 0xA0-0xFF, in byte order
// (\u00A0 = no-break space, \u00AD = soft hyphen).
const ISO_8859_2_HIGH_CHARS =
  "\u00A0Ą˘Ł¤ĽŚ§¨ŠŞŤŹ\u00ADŽŻ°ą˛ł´ľśˇ¸šşťź˝žż" +
  "ŔÁÂĂÄĹĆÇČÉĘËĚÍÎĎĐŃŇÓÔŐÖ×ŘŮÚŰÜÝŢß" +
  "ŕáâăäĺćçčéęëěíîďđńňóôőö÷řůúűüýţ˙";

const ISO_8859_2_HIGH_BYTES = new Map<number, number>(
  Array.from(ISO_8859_2_HIGH_CHARS, (char, index) => [
    char.charCodeAt(0),
    0xa0 + index,
  ]),
);

// The UPN QR standard requires the payload to be encoded as ISO 8859-2
// bytes (ECI 000004); characters outside the charset become "?".
export function encodeIso88592(input: string): Uint8Array {
  const bytes = new Uint8Array(input.length);
  for (let i = 0; i < input.length; i++) {
    const codePoint = input.charCodeAt(i);
    if (codePoint < 0x80) {
      bytes[i] = codePoint;
    } else {
      bytes[i] = ISO_8859_2_HIGH_BYTES.get(codePoint) ?? 0x3f;
    }
  }
  return bytes;
}

/**
 * Builds the Croatian Tax Authority (Porezna uprava) receipt verification URL
 * that a FINA fiscal receipt encodes into its QR code.
 *
 * Unlike Slovenia's FURS, the FINA fiscalization response does not include a
 * ready-made QR payload, so the verification URL is assembled from the
 * invoice's fiscalization identifiers. The QR encodes the JIR when the invoice
 * was fiscalized online; when only the ZKI is available (offline issuance, JIR
 * not yet assigned) the ZKI is used instead. Returns `null` when neither
 * identifier is present (e.g. an unfiscalized draft) so callers can skip the QR.
 *
 * @param jir - FINA-assigned unique invoice identifier (Jedinstveni identifikator računa)
 * @param zki - Issuer's protection code (Zaštitni kôd izdavatelja), used when no JIR exists
 * @param finaVerifyDate - Issue timestamp formatted as `YYYYMMDD_HHmm`
 * @param totalWithTax - Gross invoice total in major currency units (e.g. EUR)
 */
export function buildFinaVerificationUrl({
  jir,
  zki,
  finaVerifyDate,
  totalWithTax,
}: {
  jir?: string | null;
  zki?: string | null;
  finaVerifyDate: string;
  totalWithTax: number;
}): string | null {
  const identifier = (() => {
    if (jir) return `jir=${jir}`;
    if (zki) return `zki=${zki}`;
    return null;
  })();

  if (identifier == null) return null;

  // `izn` is the gross total in cents (lipa) without a decimal separator,
  // e.g. 70.00 EUR -> 7000.
  const amountInCents = Math.round(totalWithTax * 100);

  return `https://porezna.gov.hr/rn/?${identifier}&datv=${finaVerifyDate}&izn=${amountInCents}`;
}

export const buildUpnQrCodeString = ({
  invoiceNumber,
  payerName,
  payerStreet,
  payerCity,
  amount,
  purposeCode,
  paymentPurpose,
  dueDate, // "dd.MM.yyyy" or ""
  recipientIban,
  recipientReference,
  recipientName,
  recipientStreet,
  recipientCity,
}: {
  invoiceNumber: string;
  payerName: string;
  payerStreet: string;
  payerCity: string;
  amount: number; // The payment amount in EUR
  purposeCode: string; // A four-letter code, e.g. "RENT"
  paymentPurpose: string; // A textual description of the payment
  dueDate: string; // "dd.MM.yyyy" format or an empty string if not used
  recipientIban: string; // The recipient's IBAN (no spaces)
  recipientReference: string; // The recipient's reference (e.g. "SI12...")
  recipientName: string; // The recipient's name
  recipientStreet: string; // The recipient's street and number
  recipientCity: string; // The recipient's city
}) => {
  const leadingStyle = "UPNQR"; // 1
  const payerIban = ""; // 2
  const polog = ""; // 3
  const dvig = ""; // 4
  const payerReference = ""; // 5

  // Convert amount to an 11-digit string without decimal point.
  // For example, 81.05 => "0000008105".
  const amountStr = amountToUPNString(amount);

  const paymentDate = ""; // 10 (often empty)
  const urgent = ""; // 11

  // Field lengths below are the maximums from the UPN QR record structure
  // (names/streets/cities 33, purpose 42, reference 4+22, IBAN 34); the
  // reference must be model + number without spaces (e.g. "SI0026") and
  // the IBAN must have no spaces either.
  const paymentPurposeCode = purposeCode.trim().toUpperCase().slice(0, 4); // 12
  const paymentPurposeField = paymentPurpose.trim().slice(0, 42); // 13
  const paymentDueDate = dueDate; // 14 (e.g. "01.04.2017" or "")
  const recipientIbanField = electronicFormat(recipientIban).slice(0, 34); // 15
  const recipientReferenceField = recipientReference
    .replace(/\s+/g, "")
    .slice(0, 26); // 16

  const fields = [
    leadingStyle, // 1
    payerIban, // 2
    polog, // 3
    dvig, // 4
    payerReference, // 5
    (payerName ?? "").trim().slice(0, 33), // 6
    (payerStreet ?? "").trim().slice(0, 33), // 7
    (payerCity ?? "").trim().slice(0, 33), // 8
    amountStr, // 9
    paymentDate, // 10
    urgent, // 11
    paymentPurposeCode, // 12
    paymentPurposeField, // 13
    paymentDueDate, // 14
    recipientIbanField, // 15
    recipientReferenceField, // 16
    recipientName.trim().slice(0, 33), // 17
    recipientStreet.trim().slice(0, 33), // 18
    recipientCity.trim().slice(0, 33), // 19
  ];

  // Control sum (field 20) per the standard:
  // strlen(N1+'\n') + strlen(N2+'\n') + ... + strlen(N19+'\n'),
  // as a 3-digit zero-padded number.
  const controlSum = fields
    .reduce((sum, field) => sum + field.length + 1, 0)
    .toString()
    .padStart(3, "0");

  const payload = [...fields, controlSum].map((field) => `${field}\n`).join("");

  return payload.padEnd(UPN_QR_MAX_PAYLOAD_LENGTH, " ");
};
